<template>
  <div class="homepage-hero-container">
    <a
      :href="link"
      class="media-card"
      :style="{
        '--backgroundImageSmall': `url(${backgroundImages?.smallMedia})`,
        '--backgroundImageMedium': `url(${backgroundImages?.mediumMedia})`,
        '--backgroundImageLarge': `url(${backgroundImages?.largeMedia})`,
      }"
      @click="handleHeroClick"
    >
      <div class="media-card__image">
        <div v-if="video && !lowPower" class="video-intersection-observer">
          <Video
            :video="{
              sources: [
                {
                  url: video,
                },
              ],
            }"
            @lowPower="lowPower = true"
          >
          </Video>
        </div>
        <div class="text-container">
          <h1 class="heading" :class="classes">{{ title }}</h1>
          <span class="heading2">{{ title2 }}</span>
          <div class="description-container">
            <p v-if="description" class="description">{{ description }}</p>
            <span v-if="description2" class="description2">{{
              description2
            }}</span>
            <slot name="content" />
          </div>
          <div class="button-container">
            <Button
              id="homepage-hero-cta"
              theme="primary-light"
              tag="a"
              :href="link"
            >
              {{ buttonText }}
            </Button>
          </div>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
import Button from "@/components/ButtonV3.vue";
import Video from "@/components/Video.vue";
import { heroClickEvent } from "@/helpers/events";

export default {
  name: "HomepageBlock1",
  components: {
    Button,
    Video,
  },
  inject: ["mq"],
  props: {
    images: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    title2: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: null,
    },
    description2: {
      type: String,
      default: null,
    },
    smallFont: {
      type: Boolean,
      default: false,
    },
    link: {
      type: String,
      default: null,
    },
    buttonText: {
      type: String,
      default: null,
    },
    videoUrl: {
      type: String,
      default: null,
    },
    videoUrlMobile: {
      type: String,
      default: null,
    },
    lowPowerImages: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      lowPower: false,
    };
  },
  computed: {
    backgroundImages() {
      if (this.lowPower) {
        return this.lowPowerImages;
      }

      return this.images;
    },
    classes() {
      return {
        "heading--smallFont": this.smallFont,
      };
    },
    video() {
      if (this.mq.mdPlus) {
        return this.videoUrl;
      }
      return this.videoUrlMobile;
    },
  },
  methods: {
    handleHeroClick() {
      heroClickEvent({
        title: this.title,
        link: this.link,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.homepage-hero-container {
  position: relative;
  text-align: center;
}

.media-card {
  position: relative;
  text-decoration: none;

  @include respond-above(md) {
    text-align: center;
  }

  &__image {
    height: 92vh;
    color: $color-white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0;
    position: relative;

    background-image: var(--backgroundImageSmall);
    background-size: cover;
    background-position: top;
    background-repeat: no-repeat;

    @include respond-above(sm) {
      background-image: var(--backgroundImageMedium);
    }

    @include respond-above(md) {
      height: 100vh;
      justify-content: center;
      background-image: var(--backgroundImageLarge);
    }

    &::after {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 12%,
        rgba(0, 0, 0, 0.3) 90%
      );
    }

    .video-intersection-observer {
      position: absolute;
      top: 0;
      left: 0;
      display: block;
      width: 100%;
      height: 100%;

      :deep(.video__element) {
        object-fit: cover;
      }
    }
  }
}

.text-container {
  text-wrap: balance;
  color: $color-white;
  position: absolute;
  margin: 0 auto 60px;
  top: auto;
  left: 0;
  right: 0;
  bottom: 0;
  max-width: 800px;
  z-index: 1;
}

.heading {
  display: contents;
  @include editorial-headline(36px);
  line-height: 100%;
  @include respond-above(md) {
    @include editorial-headline(48px);
    line-height: 100%;
  }

  &--smallFont {
    max-width: 428px;
    margin: 0 auto 20px;
    @include editorial-headline(28px);
    line-height: 100%;
    @include respond-above(md) {
      margin: 0 auto 28px;
      @include editorial-headline(36px);
      line-height: 100%;
    }
  }
}

.heading2 {
  display: block;
  margin: 0 auto 20px;
  @include editorial-headline(28px);
  line-height: 100%;
  @include respond-above(md) {
    margin: 0 auto 28px;
    @include editorial-headline(36px);
    line-height: 100%;
  }
}
.description-container {
  margin-top: 20px;
}

.description {
  @include editorial-body(16px);
  display: contents;
  @include respond-above(md) {
    @include editorial-body(16px);
  }
}
.description2 {
  @include editorial-body(20px);
  font-style: italic;
  margin-bottom: 28px;
  @include respond-above(md) {
    @include editorial-body(24px);
    font-style: italic;
  }
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}
</style>
